import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FilterOption } from "@/components/Shared/FilterGroup/FilterGroup";
import { NO_SUBSIDIARY } from "../../../models/Subsidiary";
import { useOrganisation } from "../../../networking/organisation.hooks";
import { useSubsidiaries } from "../../../networking/subsidiary.hooks";

export interface SalaryBatchFilterOptions {
	pools: FilterOption[];
	subsidiaries: FilterOption[];
}

/**
 * Options for the salary batch consultant filter — the organisation's pools
 * and its subsidiaries, not derived from the consultant list (which runs into
 * the tens of thousands).
 *
 * A consequence worth knowing: options are offered even when no consultant
 * currently carries them, so a filter can legitimately match nobody.
 */
export function useSalaryBatchFilterOptions(): SalaryBatchFilterOptions & {
	isLoading: boolean;
} {
	const { t } = useTranslation();
	const { data: organisation, isLoading: isLoadingOrg } = useOrganisation();
	const { subsidiaries, isLoading: isLoadingSubsidiaries } = useSubsidiaries();

	const options = useMemo<SalaryBatchFilterOptions>(() => {
		const byName = (a: FilterOption, b: FilterOption) => a.name.localeCompare(b.name, "sv");

		return {
			pools: (organisation?.pools ?? [])
				.map((pool) => ({ key: pool.key, name: pool.name || pool.key }))
				.sort(byName),
			// The sentinel is offered first so "employed by the organisation
			// directly" is selectable alongside the real subsidiaries
			subsidiaries: [
				...(subsidiaries.length
					? [{ key: NO_SUBSIDIARY, name: t("batchFilter.noSubsidiary") }]
					: []),
				...subsidiaries.map((s) => ({ key: s.id, name: s.name })).sort(byName),
			],
		};
	}, [organisation, subsidiaries, t]);

	return {
		...options,
		isLoading: isLoadingOrg || isLoadingSubsidiaries,
	};
}
